/**
 * Wreck scans (M2-10, spec §6.3 step 1): one row per remaining part of a wreck with its type, condition, value
 * estimate, mass and anchors. Value accuracy follows the scanner Mk exactly as rock scans do (spec §5.2,
 * `mining.json scan`): Mk I a range of ± `valueSpreadMk1` around the Mk II estimate, Mk II one estimate within
 * ± `valueSpreadMk2` (a stable per-part error, not re-rolled per scan), Mk III exact. Coolant anchors are flagged at
 * every Mk, and each row carries its condition band (`salvage.json bands`, economy plan SY-1). Allocation-free
 * (D-014): it writes the caller-owned `WreckScan`.
 */
import { MAX_ANCHORS_PER_PART, MAX_PARTS_PER_WRECK, type M2Data } from '../data/m2Schemas'
import type { PartPool, WreckPool, WreckScan } from '../space/contracts'
import { seededUnit } from '../space/hash'
import { conditionBand, contentsBaseValue, partValueByIndex } from './generate'

/** The part-pool columns a scan reads. */
export type ScanPartPool = Pick<
  PartPool,
  'high' | 'alive' | 'wreck' | 'index' | 'type' | 'condition' | 'massT' | 'anchorCount' | 'cut' | 'coolant' | 'freed' | 'secured' | 'contents' | 'mk'
>

/** The M2 data a scan reads (component base values, ware prices, the scanner spreads, the condition bands). */
export type WreckScanData = Pick<M2Data, 'wares' | 'mining' | 'salvage'>

/** Salt of the stable per-part value error. */
const SALT_SCAN_ERROR = 0x5ca7e770

/**
 * Scans wreck slot `wreck` with scanner Mk `scannerMk` (1-3) into `out`: every live, unsecured part of that wreck
 * (freed ones included and flagged), up to `MAX_PARTS_PER_WRECK` rows. A cargo pod's value is its contents at base
 * prices. Returns false (and marks `out` invalid) when the wreck has no such parts.
 */
export function scanWreck(
  wrecks: Pick<WreckPool, 'seed'>,
  parts: ScanPartPool,
  wreck: number,
  scannerMk: number,
  data: WreckScanData,
  out: WreckScan,
): boolean {
  const spread1 = data.mining.scan.valueSpreadMk1
  const spread2 = data.mining.scan.valueSpreadMk2
  const bands = data.salvage.bands
  const seed = wrecks.seed[wreck]
  let rows = 0
  let totalLow = 0
  let totalHigh = 0
  for (let p = 0; p < parts.high && rows < MAX_PARTS_PER_WRECK; p++) {
    if (parts.alive[p] === 0 || parts.secured[p] !== 0 || parts.wreck[p] !== wreck) continue
    const type = parts.type[p]
    const condition = parts.condition[p]
    const contents = parts.contents[p]
    const value = contents !== null ? contentsBaseValue(contents, data) : partValueByIndex(type, condition, data)
    let low = value
    let high = value
    if (scannerMk < 3) {
      const estimate = value * (1 + (seededUnit(seed, parts.index[p], SALT_SCAN_ERROR) * 2 - 1) * spread2)
      low = scannerMk <= 1 ? estimate * (1 - spread1) : estimate
      high = scannerMk <= 1 ? estimate * (1 + spread1) : estimate
    }
    const base = p * MAX_ANCHORS_PER_PART
    const n = parts.anchorCount[p]
    let cut = 0
    let coolant = 0
    for (let a = 0; a < n; a++) {
      if (parts.cut[base + a] >= 1) cut++
      else if (parts.coolant[base + a] !== 0) coolant = 1
    }
    out.part[rows] = p
    out.type[rows] = type
    out.condition[rows] = condition
    out.band[rows] = conditionBand(condition, bands)
    out.partMk[rows] = parts.mk[p]
    out.valueLow[rows] = low
    out.valueHigh[rows] = high
    out.massT[rows] = parts.massT[p]
    out.anchors[rows] = n
    out.anchorsCut[rows] = cut
    out.coolant[rows] = coolant
    out.freed[rows] = parts.freed[p]
    totalLow += low
    totalHigh += high
    rows++
  }
  out.valid = rows > 0
  out.wreck = wreck
  out.mk = scannerMk
  out.partCount = rows
  out.totalLow = totalLow
  out.totalHigh = totalHigh
  return out.valid
}
